import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, ShoppingCart, Check, Trash2, Package, Filter, Clock, CheckSquare } from 'lucide-react';

const CATEGORIES = [
  { value: 'bebidas', label: 'Bebidas' },
  { value: 'comida', label: 'Comida' },
  { value: 'limpieza', label: 'Limpieza' },
  { value: 'menaje', label: 'Menaje' },
  { value: 'otros', label: 'Otros' },
];

const UNITS = ['unidades', 'cajas', 'kg', 'litros', 'packs', 'bolsas'];

const CATEGORY_COLORS = {
  bebidas: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  comida: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  limpieza: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  menaje: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  otros: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

export default function Orders() {
  const { user, hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [form, setForm] = useState({ product_name: '', quantity: 1, unit: 'unidades', notes: '', category: 'otros' });
  const [batchLoading, setBatchLoading] = useState(false);
  const canMarkOrdered = hasPermission('orders.mark_ordered');
  const canCreate = hasPermission('orders.create');

  const load = useCallback(async () => {
    try {
      const [ordersRes, historyRes] = await Promise.all([
        API.get('/orders'),
        API.get('/order-history'),
      ]);
      setItems(ordersRes.data);
      setOrderHistory(historyRes.data);
    } catch { toast.error('Error cargando pedidos'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ product_name: '', quantity: 1, unit: 'unidades', notes: '', category: 'otros' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.product_name.trim()) { toast.error('El nombre del producto es obligatorio'); return; }
    try {
      await API.post('/orders', form);
      toast.success('Producto anadido a la lista');
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pendingItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingItems.map(i => i.id)));
    }
  };

  const handleBatchMark = async () => {
    if (selectedIds.size === 0) return;
    setBatchLoading(true);
    try {
      await API.post('/orders/batch-mark', { item_ids: Array.from(selectedIds) });
      toast.success(`${selectedIds.size} producto(s) marcados como pedidos`);
      setSelectedIds(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al marcar pedidos');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleUnmarkOrdered = async (oid) => {
    try {
      await API.post(`/orders/${oid}/unmark-ordered`);
      toast.success('Desmarcado');
      load();
    } catch { toast.error('Error'); }
  };

  const handleDelete = async (oid) => {
    try {
      await API.delete(`/orders/${oid}`);
      toast.success('Producto eliminado');
      load();
    } catch { toast.error('Error'); }
  };

  const filtered = filterCategory === 'all'
    ? items
    : items.filter(i => i.category === filterCategory);

  const pendingItems = filtered.filter(i => i.status === 'pendiente');
  const orderedItems = filtered.filter(i => i.status === 'pedido');

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50">Pedidos</h1>
          <p className="text-sm text-zinc-500">Lista de la compra del bar</p>
        </div>
        <div className="flex gap-2">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="bg-zinc-950/50 border-white/10 w-36 h-9" data-testid="filter-category-select">
              <Filter className="w-3.5 h-3.5 mr-1 text-zinc-500" />
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-white/10">
              <SelectItem value="all">Todas</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {canCreate && (
            <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="add-product-button">
              <Plus className="w-4 h-4 mr-2" /> Anadir Producto
            </Button>
          )}
        </div>
      </div>

      {/* Pending items with batch selection */}
      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base text-zinc-200 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-amber-500" />
            Por Pedir
          </CardTitle>
          <div className="flex items-center gap-2">
            {canMarkOrdered && pendingItems.length > 0 && (
              <button
                onClick={toggleSelectAll}
                className="text-xs text-zinc-500 hover:text-amber-400 transition-colors"
                data-testid="select-all-toggle"
              >
                {selectedIds.size === pendingItems.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </button>
            )}
            <Badge variant="outline" className="text-xs text-amber-500/70 border-amber-500/20">
              {pendingItems.length} items
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {pendingItems.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
              <p className="text-sm text-zinc-600">No hay productos pendientes</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pendingItems.map(item => (
                <div key={item.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors group ${
                    selectedIds.has(item.id)
                      ? 'bg-amber-500/5 border-amber-500/20'
                      : 'bg-zinc-900/40 border-white/5 hover:border-white/10'
                  }`}
                  data-testid={`order-item-${item.id}`}>
                  {canMarkOrdered && (
                    <Checkbox
                      checked={selectedIds.has(item.id)}
                      onCheckedChange={() => toggleSelect(item.id)}
                      className="border-zinc-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                      data-testid={`checkbox-${item.id}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-zinc-200">{item.product_name}</span>
                      <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLORS[item.category] || CATEGORY_COLORS.otros}`}>
                        {item.category}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-amber-500/70 font-medium">{item.quantity} {item.unit}</span>
                      {item.notes && <span className="text-xs text-zinc-600">- {item.notes}</span>}
                    </div>
                    <span className="text-[10px] text-zinc-600">Anadido por {item.added_by_name}</span>
                  </div>
                  {(canMarkOrdered || item.added_by === user?.id) && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 shrink-0"
                      onClick={() => handleDelete(item.id)} data-testid={`delete-order-${item.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Batch action bar */}
          {canMarkOrdered && selectedIds.size > 0 && (
            <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="batch-action-bar">
              <span className="text-sm text-amber-400 font-medium">
                <CheckSquare className="w-4 h-4 inline mr-1.5" />
                {selectedIds.size} seleccionado(s)
              </span>
              <Button
                onClick={handleBatchMark}
                disabled={batchLoading}
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                data-testid="batch-mark-ordered-button"
              >
                <Check className="w-4 h-4 mr-2" />
                {batchLoading ? 'Marcando...' : 'Marcar como Pedido'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ordered items */}
      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base text-zinc-200 flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-400" />
            Ya Pedido
          </CardTitle>
          <Badge variant="outline" className="text-xs text-emerald-400/70 border-emerald-500/20">
            {orderedItems.length} items
          </Badge>
        </CardHeader>
        <CardContent>
          {orderedItems.length === 0 ? (
            <p className="text-sm text-zinc-600 text-center py-6">No hay productos pedidos</p>
          ) : (
            <div className="space-y-2">
              {orderedItems.map(item => (
                <div key={item.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10 group"
                  data-testid={`order-item-${item.id}`}>
                  {canMarkOrdered && (
                    <button
                      onClick={() => handleUnmarkOrdered(item.id)}
                      className="w-5 h-5 rounded border-2 border-emerald-500/50 bg-emerald-500/20 flex items-center justify-center shrink-0 transition-colors hover:border-emerald-500"
                      data-testid={`unmark-ordered-${item.id}`}
                      aria-label="Desmarcar pedido"
                    >
                      <Check className="w-3 h-3 text-emerald-400" />
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-zinc-400 line-through">{item.product_name}</span>
                      <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLORS[item.category] || CATEGORY_COLORS.otros}`}>
                        {item.category}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-zinc-600">{item.quantity} {item.unit}</span>
                      {item.ordered_by_name && (
                        <span className="text-xs text-emerald-500/60">Pedido por {item.ordered_by_name}</span>
                      )}
                    </div>
                  </div>
                  {canMarkOrdered && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 shrink-0"
                      onClick={() => handleDelete(item.id)} data-testid={`delete-order-${item.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order History */}
      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base text-zinc-200 flex items-center gap-2">
            <Clock className="w-4 h-4 text-zinc-400" />
            Historial de Pedidos
          </CardTitle>
          <Badge variant="outline" className="text-xs text-zinc-400/70 border-zinc-500/20">
            {orderHistory.length} registros
          </Badge>
        </CardHeader>
        <CardContent>
          {orderHistory.length === 0 ? (
            <p className="text-sm text-zinc-600 text-center py-6" data-testid="no-history">Aun no hay pedidos registrados</p>
          ) : (
            <div className="space-y-3" data-testid="order-history-list">
              {orderHistory.map(batch => (
                <div key={batch.id} className="p-3 rounded-lg bg-zinc-900/40 border border-white/5" data-testid={`history-batch-${batch.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-400 font-medium">{formatDate(batch.ordered_at)}</span>
                    <span className="text-xs text-zinc-500">por {batch.ordered_by_name}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {batch.items.map(bi => (
                      <Badge key={bi.id} variant="outline" className="text-[11px] text-zinc-300 border-zinc-700 bg-zinc-800/50">
                        {bi.product_name} ({bi.quantity} {bi.unit})
                      </Badge>
                    ))}
                  </div>
                  {batch.notes && (
                    <p className="text-xs text-zinc-500 mt-1.5 italic">{batch.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add product dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 sm:max-w-md" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-heading">Anadir Producto</DialogTitle>
            <DialogDescription className="text-zinc-500">Anade un producto a la lista de pedidos</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Producto</Label>
              <Input value={form.product_name} onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
                placeholder="Ej: Cerveza Estrella Galicia"
                className="bg-zinc-950/50 border-white/10" data-testid="order-product-input" aria-label="Nombre del producto" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Cantidad</Label>
                <Input type="number" min="0.5" step="0.5" value={form.quantity}
                  onChange={e => setForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 1 }))}
                  className="bg-zinc-950/50 border-white/10" data-testid="order-quantity-input" aria-label="Cantidad" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Unidad</Label>
                <Select value={form.unit} onValueChange={v => setForm(f => ({ ...f, unit: v }))}>
                  <SelectTrigger className="bg-zinc-950/50 border-white/10" data-testid="order-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10">
                    {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Categoria</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="bg-zinc-950/50 border-white/10" data-testid="order-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10">
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Notas (opcional)</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Ej: Para el fin de semana"
                className="bg-zinc-950/50 border-white/10" aria-label="Notas" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200">Cancelar</Button>
            <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="order-save-button">Anadir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
